// 麻将 (推倒胡) in the 牌桌 panel (public/cards.js mounts it; the rules: src/cards/mahjong.ts). The table seen from my
// seat (a watcher sits at seat 0's place): each player's discards in front of them, the others' hands face down with
// their melds, the wall's count and everyone's points in the middle; my own hand big along the bottom (tap a tile to
// pick it, tap it again to discard; double-click works too) and my moves as buttons: 胡 / 自摸, 碰, 杠, 吃, 过. When a
// hand ends every hand is shown with the 番 and the points. 补位 fills the empty seats with bots between hands.
// Only what the office sent me is drawn: my own hand, the others' counts, every hand once the hand is over.
// Tiles come from mahjong.png (tools/cards/draw-mahjong.mjs).
import { sized, el, rr, fit, seatXY, nameBox, boxAt, INK, CREAM, YELLOW, WIN } from '/cards-common.js';
import { t, has } from '/i18n.js';
const tr = t; // (some functions here have a local t: a tile)

// ---- the tiles' pictures (tools/cards/draw-mahjong.mjs: rows 万 条 筒, then 东南西北白发中, the back, face down)
export const TILE_W = 18, TILE_H = 24;
const sheet = new Image(); sheet.src = new URL('./mahjong.png', import.meta.url).pathname;
const ready = new Promise((ok) => { sheet.onload = ok; sheet.onerror = ok; });
/** Where tile `t` ("5m", "7z"; "" or null: the back; "down": lying face down) is in the sheet: [sx, sy]. */
export function tileRect(t) {
  if (t === 'down') return [8 * TILE_W, 3 * TILE_H];
  if (!t) return [7 * TILE_W, 3 * TILE_H];
  return [(+t[0] - 1) * TILE_W, 'mspz'.indexOf(t[1]) * TILE_H];
}
/** Draw tile `t` with its top-left at (x, y) (of the turned box), `k` times its size, turned `rot` quarter turns
 *  clockwise (1: a player on the left, 2: across, 3: on the right); `mark`: a yellow outline (the last discard). */
function drawTile(g, t, x, y, k = 1, rot = 0, mark = false, dim = false) {
  const [sx, sy] = tileRect(t), w = TILE_W * k, h = TILE_H * k;
  g.save(); g.imageSmoothingEnabled = false; g.globalAlpha = dim ? 0.5 : 1;
  const bw = rot % 2 ? h : w, bh = rot % 2 ? w : h;
  g.translate(Math.round(x + bw / 2), Math.round(y + bh / 2)); g.rotate((rot * Math.PI) / 2);
  if (sheet.complete && sheet.naturalWidth) g.drawImage(sheet, sx, sy, TILE_W, TILE_H, -w / 2, -h / 2, w, h);
  else { g.fillStyle = '#fffdf6'; g.fillRect(-w / 2, -h / 2, w, h); g.strokeStyle = INK; g.strokeRect(-w / 2, -h / 2, w, h); }
  if (mark) { g.strokeStyle = YELLOW; g.lineWidth = 2; g.strokeRect(-w / 2 - 1, -h / 2 - 1, w + 2, h + 2); }
  g.restore();
}

// (the words: mahjong.num.* 一…九, mahjong.suit.* 万 条 筒, mahjong.tile.* 三万, mahjong.honor.* 东 … 中, mahjong.run.* 一二三万)
const SUITS = ['m', 's', 'p'];
/** A suit (m s p) in words: 万 条 筒. */
const suitName = (s) => (SUITS.includes(s) ? tr(`mahjong.suit.${s}`) : '');
const num = (n) => tr(`mahjong.num.${n}`);
/** A tile in words: 三万, 七条, 五筒, 东, 中. */
/** A 番 from its id (f.id; x: how many, 根 x2); one from an older office: its words (f.name). */
const fanName = (f) => (f.id && has(`mahjong.fanName.${f.id}`) ? tr(`mahjong.fanName.${f.id}`, { x: f.x ?? 1 }) : f.name);
export const tileName = (t) => (!t ? '' : t[1] === 'z' ? tr(`mahjong.honor.${t[0]}`) : SUITS.includes(t[1]) ? tr(`mahjong.tile.${t[1]}`, { num: num(+t[0]) }) : t);
const runName = (lo) => (SUITS.includes(lo[1]) ? tr(`mahjong.run.${lo[1]}`, { a: num(+lo[0]), b: num(+lo[0] + 1), c: num(+lo[0] + 2) }) : lo);
/** The moves' buttons: a move -> its words' key (hu: by the stage, a claim or my own draw); vars from the move's tile. */
const MOVES = {
  que: (t) => ['mahjong.move.que', { suit: suitName(t) }],
  hu: (t, stage) => [stage === 'claim' ? 'mahjong.move.hu' : 'mahjong.move.zimo'],
  peng: () => ['mahjong.move.peng'], gang: () => ['mahjong.move.gang'], pass: () => ['mahjong.move.pass'],
  angang: (t) => ['mahjong.move.angang', { tile: tileName(t) }], bugang: (t) => ['mahjong.move.bugang', { tile: tileName(t) }],
  chi: (t) => (t ? ['mahjong.move.chiRun', { run: runName(t) }] : ['mahjong.move.chi']),
};
/** A move as a button's words. */
function moveText(mv, stage) {
  const [k, t] = mv.split(' '), m = MOVES[k]?.(t, stage);
  return m ? tr(...m) : mv;
}
/** A log event's words (mahjong.evt.<k>: discard chi peng mgang agang bgang hu zimo draw_game left deal que). */
const evtText = (k) => (has(`mahjong.evt.${k}`) ? tr(`mahjong.evt.${k}`) : k);

/** The rule sets (玩法) a table can be set up with (src/cards/mahjong.ts RULESETS has the same ids), and what the 玩法
 *  card shows for each: the rules in short lines and the 番 table. */
// (the words are in the catalogs, public/locales: mahjong.set.*, mahjong.rules.*, mahjong.fan.*)
const RULESETS = ['tuidao', 'xuezhan'].map((id) => ({ id, get name() { return t(`mahjong.set.${id}.name`); }, get blurb() { return t(`mahjong.set.${id}.blurb`); } }));
/** Each rule set's 玩法 card: sections (heading key, how many lines) and its 番 table (row ids). */
const SPEC = {
  tuidao: { sections: [['tiles', 2], ['win', 2], ['claims', 3], ['fan'], ['pay', 3]], fan: ['pinghu', 'qidui', 'pengpeng', 'hunyise', 'qingyise', 'ziyise', 'zimo', 'gangkai', 'haidi'] },
  xuezhan: { sections: [['tiles', 2], ['que', 2], ['win', 3], ['fan'], ['pay', 4]], fanH: 'fanXz', fan: ['pinghu', 'duidui', 'qingyise', 'qidui', 'jingou', 'gen', 'zimo', 'gangkai', 'gangpao', 'haidi'], other: ['mahjong.rules.xuezhan.noSwap'] },
};
function rulesOf(id) {
  const sp = SPEC[id] ?? SPEC.tuidao;
  const sections = sp.sections.map(([h, n]) => (h === 'fan'
    ? { h: t(`mahjong.rules.h.${sp.fanH ?? 'fan'}`), table: sp.fan.map((f) => ['name', 'n', 'note'].map((x) => (has(`mahjong.fan.${id}.${f}.${x}`) ? t(`mahjong.fan.${id}.${f}.${x}`) : ''))) }
    : { h: t(`mahjong.rules.h.${h}`), lines: Array.from({ length: n }, (_, k) => t(`mahjong.rules.${id}.${h}.${k + 1}`)) }));
  sections.push({ h: t('mahjong.rules.h.other'), lines: [...(sp.other ?? []), 'mahjong.rules.clock', 'mahjong.rules.resume'].map((k) => t(k)) });
  return { title: t(`mahjong.set.${id}.name`), sections };
}

const W = 640, H = 360, HW = 640, HH = 56;

export default {
  kind: 'mahjong', get name() { return t('mahjong.name'); }, icon: 'cards', seats: 4, min: 4, bots: true, wide: true,
  /** 玩法: picked by whoever sets the table up (public/cards.js shows the choice and the 玩法 card). */
  options: [{ key: 'rules', get label() { return t('mahjong.opt.rules'); }, choices: RULESETS }],
  rules: (opts) => rulesOf(opts?.rules ?? 'tuidao'),
  /** The 玩法's name after the game's (麻将 · 推倒胡), as the office's kindName has it (src/cards/mahjong.ts label). */
  label: (opts) => t(`mahjong.set.${SPEC[opts?.rules] ? opts.rules : 'tuidao'}.name`),
  get blurb() { return t('mahjong.blurb'); },
  mount(root, api) {
    let st = null, sel = -1;
    const canvas = el('canvas', 'ctfelt ctmjtable'), wrap = el('div', 'ctfeltwrap'); wrap.append(canvas);
    const handCanvas = el('canvas', 'ctmjhand'), hand = el('div', 'ctmjhandrow'); hand.append(handCanvas);
    const acts = el('div', 'gmbtns ctacts ctmjacts');
    const extra = el('div', 'gmbtns ctmjextra'), botsOn = el('button'), botsOff = el('button');
    extra.append(botsOn, botsOff);
    const log = el('div', 'ctlog ctmjlog');
    root.append(wrap, hand, acts, extra, log);
    botsOn.onclick = () => api.bots(true);
    botsOff.onclick = () => api.bots(false);

    const v = () => st?.view ?? {};
    const live = () => st?.phase === 'playing' && v().live;
    /** Where seat i is drawn: 0 bottom, 1 left, 2 top, 3 right (from my seat; a watcher sees seat 0 at the bottom). */
    const bottom = () => Math.max(0, api.mySeat());
    const rel = (i) => (i - bottom() + 4) % 4;
    const at = (i) => seatXY(i, 4, bottom(), W, H);
    const name = (i) => api.name(i);
    const discardable = () => (st?.legal ?? []).some((m) => m.startsWith('d '));
    const emptyAt = (e) => {
      if (!st || api.mySeat() >= 0) return -1;
      const i = boxAt(canvas, e, W, H, 4, at);
      return i >= 0 && !st.seats[i] ? i : -1;
    };
    canvas.addEventListener('click', (e) => { const i = emptyAt(e); if (i >= 0) api.sitAt(i); });
    canvas.addEventListener('pointermove', (e) => { canvas.style.cursor = emptyAt(e) >= 0 ? 'pointer' : ''; });

    // ---- my hand: tap to pick, tap again (or double-click) to discard
    function handLayout() {
      const h = v().hand ?? [], drawn = v().drawn, k = 2, out = [];
      // the tile just drawn stands a little apart at the right
      const di = drawn ? h.lastIndexOf(drawn) : -1, order = h.map((t, i) => i).filter((i) => i !== di);
      if (di >= 0) order.push(di);
      let x = 4;
      order.forEach((i, n) => { if (di >= 0 && n === order.length - 1) x += 10; out.push({ t: h[i], x, y: 4, w: TILE_W * k, h: TILE_H * k }); x += TILE_W * k; });
      return { tiles: out, end: x };
    }
    function hit(e) {
      const r = handCanvas.getBoundingClientRect(), x = ((e.clientX - r.left) / r.width) * HW;
      return handLayout().tiles.findIndex((b) => x >= b.x && x < b.x + b.w);
    }
    handCanvas.addEventListener('click', (e) => {
      if (!discardable()) return;
      const i = hit(e); if (i < 0) return;
      if (sel === i) { discard(i); return; }
      sel = i; drawHand();
    });
    handCanvas.addEventListener('dblclick', (e) => { const i = hit(e); if (i >= 0 && discardable()) discard(i); });
    function discard(i) {
      const t = handLayout().tiles[i]?.t; sel = -1;
      if (t && (st.legal ?? []).includes(`d ${t}`)) api.send(`d ${t}`);
      drawHand();
    }
    function drawHand() {
      const g = sized(handCanvas, HW, HH), mine = api.mySeat() >= 0, b = bottom(), vw = v();
      handCanvas.style.aspectRatio = `${HW} / ${HH}`;
      let end = 4;
      if (mine && Array.isArray(vw.hand)) {
        const lay = handLayout();
        // my turn: the tiles I may not discard now (定缺: the given-up suit goes first) are dimmed
        const ds = (st?.legal ?? []).filter((m) => m.startsWith('d ')).map((m) => m.slice(2));
        lay.tiles.forEach((tb, i) => drawTile(g, tb.t, tb.x, tb.y + (i === sel ? -4 : 4) + 0, 2, 0, false, ds.length > 0 && !ds.includes(tb.t)));
        end = lay.end;
      } else {
        // a watcher: seat 0's hand face down (or shown, once the hand is over)
        const shown = vw.result?.hands?.[b] ?? vw.shown?.[b], n = shown ? shown.length : vw.counts?.[b] ?? 0;
        for (let i = 0; i < n; i++) drawTile(g, shown ? shown[i] : '', 4 + i * TILE_W * 1.5, 8, 1.5);
        end = 4 + n * TILE_W * 1.5;
      }
      // my melds after the hand
      let x = end + 12;
      for (const m of vw.melds?.[b] ?? []) { for (const t of m.t) { drawTile(g, t || 'down', x, HH - TILE_H - 4, 1); x += TILE_W; } x += 6; }
    }

    // ---- the table
    /** The hand line of seat i (not mine): its tiles face down (shown when the hand is over), then its melds. */
    function line(g, i) {
      const r = rel(i), vw = v(), shown = vw.result?.hands?.[i] ?? vw.shown?.[i], n = shown ? shown.length : vw.counts?.[i] ?? 0;
      const tiles = [...Array.from({ length: n }, (_, k) => (shown ? shown[k] : '')), null];
      for (const m of vw.melds?.[i] ?? []) tiles.push(...m.t.map((t) => t || 'down'), null);
      const count = tiles.filter((t) => t !== null).length, gaps = tiles.filter((t) => t === null).length;
      const avail = r === 2 ? 360 : 290, stepFull = TILE_W, step = Math.min(stepFull, (avail - gaps * 6) / Math.max(1, count));
      const len = count * step + gaps * 6;
      let p = 0;
      for (const t of tiles) {
        if (t === null) { p += 6; continue; }
        // along the line, as that player sees it: left to right from their side
        if (r === 2) drawTile(g, t, W / 2 + len / 2 - p - TILE_W, 4, 1, 2);
        else if (r === 1) drawTile(g, t, 4, H / 2 - len / 2 + p, 1, 1);
        else if (r === 3) drawTile(g, t, W - 4 - TILE_H, H / 2 + len / 2 - p - TILE_W, 1, 3);
        p += step;
      }
    }
    /** Seat i's discards in rows of 8 in front of them, the first row nearest the middle. */
    function river(g, i) {
      const r = rel(i), tiles = v().river?.[i] ?? [], last = v().last, per = 8;
      tiles.forEach((t, k) => {
        const row = Math.floor(k / per), col = k % per, mark = !!last && last.seat === i && k === tiles.length - 1;
        if (r === 0) drawTile(g, t, W / 2 - (per * TILE_W) / 2 + col * TILE_W, 236 + row * TILE_H, 1, 0, mark);
        else if (r === 2) drawTile(g, t, W / 2 + (per * TILE_W) / 2 - (col + 1) * TILE_W, 100 - row * TILE_H, 1, 2, mark);
        else if (r === 1) drawTile(g, t, 224 - (row + 1) * TILE_H, H / 2 - (per * TILE_W) / 2 + col * TILE_W, 1, 1, mark);
        else drawTile(g, t, 416 + row * TILE_H, H / 2 + (per * TILE_W) / 2 - (col + 1) * TILE_W, 1, 3, mark);
      });
    }
    function centre(g) {
      const vw = v(), x = W / 2 - 92, y = 128, w = 184, h = 104;
      rr(g, x, y, w, h, 10); g.fillStyle = '#1f5a35'; g.fill(); g.strokeStyle = INK; g.lineWidth = 2; g.stroke();
      g.textBaseline = 'middle'; g.font = 'bold 12px system-ui';
      // each player's name and points on their side of the box; whose turn in yellow (not during a claim: secret)
      const turn = live() && vw.stage === 'discard' ? vw.turn : -1, won = vw.result?.winner;
      for (let i = 0; i < 4; i++) {
        if (!st?.seats?.[i] && !vw.scores) continue;
        // 血战: 缺X once everyone has picked (✓ 已定 before that), 已胡 for those who won
        const tag = vw.que ? (vw.que[i] ? tr('mahjong.tag.que', { suit: suitName(vw.que[i]) }) : vw.queDone?.[i] && live() ? tr('mahjong.tag.queDone') : '') : '', out = vw.won?.[i] ? tr('mahjong.tag.won') : '';
        const who = vw.hands && vw.dealer === i ? tr('mahjong.dealer', { name: name(i) }) : name(i);
        const r = rel(i), text = fit(g, !st?.seats?.[i] ? tr('cards.seat.empty') : `${who} ${vw.scores?.[i] ?? 0}${tag}${out}`, r % 2 ? 84 : 170);
        const [tx, ty, al] = r === 0 ? [W / 2, y + h - 12, 'center'] : r === 2 ? [W / 2, y + 12, 'center'] : r === 1 ? [x + 6, y + h / 2 - 12, 'left'] : [x + w - 6, y + h / 2 - 12, 'right'];
        g.textAlign = al; g.fillStyle = i === turn ? YELLOW : i === won || vw.won?.[i] ? WIN : CREAM; g.fillText(text, tx, ty);
      }
      g.textAlign = 'center'; g.fillStyle = CREAM; g.font = '11px system-ui';
      if (live() && vw.stage === 'que') g.fillText(tr('mahjong.que.center'), W / 2, y + h / 2 + 4);
      if (live() && vw.stage === 'claim' && vw.last) g.fillText(fit(g, tr('mahjong.discarded', { name: name(vw.last.seat), tile: tileName(vw.last.tile) }), w - 12), W / 2, y + h / 2 + 4);
      g.fillText(fit(g, vw.hands ? tr('mahjong.center.hand', { n: vw.hands, wall: vw.wall ?? 0 }) : tr('mahjong.center.wait'), w - 12), W / 2, y + h / 2 + 22);
    }
    /** The hand's end: who won and how, the 番, each player's points. */
    function result(g) {
      const r = v().result; if (!r || live()) return;
      if (r.end === 'xz') return resultXz(g, r);
      const x = W / 2 - 150, y = 116, w = 300, h = 128;
      rr(g, x, y, w, h, 12); g.fillStyle = 'rgba(255,248,231,.96)'; g.fill(); g.strokeStyle = INK; g.lineWidth = 3; g.stroke();
      g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.font = 'bold 15px system-ui';
      const head = r.end === 'hu' ? (r.self ? tr('mahjong.result.self', { name: name(r.winner) }) : tr('mahjong.result.from', { name: name(r.winner), from: name(r.from) })) : tr(r.end === 'draw' ? 'mahjong.result.draw' : 'mahjong.result.void');
      g.fillText(fit(g, head, w - 20), W / 2, y + 18);
      g.font = '13px system-ui';
      if (r.end === 'hu') g.fillText(fit(g, tr('mahjong.result.fan', { fans: r.fan.map((f) => tr('mahjong.fanN', { name: fanName(f), n: f.n })).join(' + '), total: r.total }), w - 20), W / 2, y + 42);
      if (r.tile) drawTile(g, r.tile, W / 2 - TILE_W / 2, y + 54, 1);
      g.font = '12px system-ui';
      const parts = [0, 1, 2, 3].map((i) => `${fit(g, name(i), 60)} ${r.delta[i] > 0 ? '+' : ''}${r.delta[i]}`);
      g.fillText(parts.slice(0, 2).join('   '), W / 2, y + 96); g.fillText(parts.slice(2).join('   '), W / 2, y + 114);
    }
    /** 血战's end: every win in order (番 and points), the 流局 settlements, each player's points on the hand. */
    function resultXz(g, r) {
      const wins = r.wins ?? [], cha = r.cha ?? [], x = W / 2 - 170, w = 340, h = 64 + wins.length * 20 + (cha.length ? 20 : 0) + 36, y = Math.max(8, H / 2 - h / 2);
      rr(g, x, y, w, h, 12); g.fillStyle = 'rgba(255,248,231,.96)'; g.fill(); g.strokeStyle = INK; g.lineWidth = 3; g.stroke();
      g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.font = 'bold 15px system-ui';
      g.fillText(wins.length ? tr('mahjong.xz.won', { n: wins.length }) + (wins.length < 3 ? tr('mahjong.xz.wallOut') : '') : tr('mahjong.xz.draw'), W / 2, y + 18);
      g.font = '12px system-ui';
      wins.forEach((wn, k) => g.fillText(fit(g, tr(wn.self ? 'mahjong.xz.winSelf' : 'mahjong.xz.winFrom', { name: name(wn.seat), from: wn.self ? '' : name(wn.from), tile: tileName(wn.tile), fans: wn.fan.map(fanName).join(' '), total: wn.total, pts: wn.pts }), w - 16), W / 2, y + 44 + k * 20));
      let yy = y + 44 + wins.length * 20;
      if (cha.length) {
        const seen = [...new Set(cha.map((c) => tr(`mahjong.cha.${c.k}`)))];
        g.fillText(fit(g, tr('mahjong.cha.title', { list: seen.join(' ') }), w - 16), W / 2, yy); yy += 20;
      }
      const parts = [0, 1, 2, 3].map((i) => `${fit(g, name(i), 56)} ${r.delta[i] > 0 ? '+' : ''}${r.delta[i]}`);
      g.fillText(parts.slice(0, 2).join('   '), W / 2, yy + 4); g.fillText(parts.slice(2).join('   '), W / 2, yy + 22);
    }
    function drawTable() {
      const g = sized(canvas, W, H);
      canvas.style.aspectRatio = `${W} / ${H}`;
      g.fillStyle = '#2f7d4a'; rr(g, 0, 0, W, H, 12); g.fill();
      if (!st) return;
      for (let i = 0; i < 4; i++) { river(g, i); if (rel(i) !== 0) line(g, i); }
      centre(g); result(g);
      // Show each open place; an unseated viewer can choose it, including in the empty-table preview.
      for (let i = 0; i < 4; i++) if (!st.seats[i]) {
        const [x, y] = at(i); nameBox(g, x, y, { name: null, empty: tr(api.mySeat() < 0 ? 'cards.seat.sitHere' : 'cards.seat.empty') });
      }
    }

    function buttons() {
      const legal = (st?.legal ?? []).filter((m) => !m.startsWith('d ')), stage = v().stage;
      acts.replaceChildren(...legal.map((mv) => {
        const b = el('button', mv === 'hu' ? 'gmprimary' : mv === 'pass' ? '' : 'ctmjclaim', moveText(mv, stage));
        b.onclick = () => { api.send(mv); sel = -1; };
        return b;
      }));
      acts.style.display = legal.length ? '' : 'none';
    }
    function logText() {
      const ev = (v().log ?? []).filter((e) => e.k !== 'draw').slice(-5);
      // (que: everyone has picked a suit to drop; its seat is the dealer, who leads)
      log.replaceChildren(...ev.map((e) => el('div', '', e.k === 'que' ? tr('mahjong.log.que', { name: name(e.seat) }) : e.tile ? tr('mahjong.log.tile', { name: name(e.seat), evt: evtText(e.k), tile: tileName(e.tile) }) : tr('mahjong.log', { name: name(e.seat), evt: evtText(e.k) }))));
      log.style.display = ev.length ? '' : 'none';
    }
    function render(s) {
      if (s) { if (st && (st.round !== s.round || (s.view?.hand ?? []).join() !== (st.view?.hand ?? []).join())) sel = -1; st = s; }
      if (!st) return;
      const seated = api.mySeat() >= 0, between = st.phase !== 'playing', seats = api.seats();
      botsOn.textContent = tr('mahjong.bots.on'); botsOff.textContent = tr('mahjong.bots.off');
      botsOn.style.display = seated && between && seats.some((p) => !p) ? '' : 'none';
      botsOff.style.display = seated && between && seats.some((p) => p?.id?.startsWith('bot:')) ? '' : 'none';
      hand.style.display = st.phase === 'waiting' && !v().hands ? 'none' : '';
      buttons(); logText();
      ready.then(() => { drawTable(); drawHand(); });
      drawTable(); drawHand();
    }
    return {
      render,
      tick() {},
      status(s) {
        const vw = s.view ?? {}, r = vw.result;
        if (s.phase === 'over' && r?.end === 'xz') return r.wins?.length ? tr('mahjong.xz.won', { n: r.wins.length }) : tr('mahjong.xz.draw');
        if (s.phase === 'over' && r) return r.end === 'hu' ? tr(r.self ? 'mahjong.status.self' : 'mahjong.status.won', { name: name(r.winner), n: r.total }) : tr(r.end === 'draw' ? 'mahjong.result.draw' : 'mahjong.status.void');
        if (s.phase !== 'playing') return null;
        const legal = s.legal ?? [];
        if (vw.stage === 'que') return tr(legal.length ? 'mahjong.que.pick' : 'mahjong.que.wait');
        if (vw.won?.[api.mySeat()]) return tr('mahjong.xz.youWon');
        if (vw.stage === 'claim' && legal.length) return tr('mahjong.claim.ask', { name: name(vw.last?.seat), tile: tileName(vw.last?.tile) });
        if (legal.some((m) => m.startsWith('d '))) return sel >= 0 ? tr('mahjong.discard.again') : vw.que?.[api.mySeat()] && (vw.hand ?? []).some((x) => x[1] === vw.que[api.mySeat()]) ? tr('mahjong.que.discardFirst', { suit: suitName(vw.que[api.mySeat()]) }) : tr('mahjong.discard.turn');
        if (vw.stage === 'claim') return tr('mahjong.claim.wait');
        return tr('mahjong.turnOf', { name: name(vw.turn) });
      },
      leaveWarn(s) { if (s.phase !== 'playing') return null; const people = (s.seats ?? []).filter((p) => p && !String(p.id).startsWith('bot:')).length; return t(people > 1 ? 'mahjong.leave.bot' : 'mahjong.leave.last'); },
    };
  },
};
